import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Search, ChevronRight, Plane, Plus } from 'lucide-react';

const STATUS_LABELS = {
  NEW: 'Nouveau', PENDING_REVIEW: 'En attente', ELIGIBLE: 'Éligible',
  IN_PROGRESS: 'En cours', NEGOTIATING: 'Négociation', WON: 'Gagné',
  PAID: 'Payé', LOST: 'Perdu', INELIGIBLE: 'Non éligible',
};

const STATUS_COLORS = {
  NEW: '#9CA3AF', PENDING_REVIEW: '#E8920A', ELIGIBLE: '#0D9F57',
  IN_PROGRESS: '#3B82F6', NEGOTIATING: '#8B5CF6', WON: '#0D9F57',
  PAID: '#0D1B3E', LOST: '#DC2626', INELIGIBLE: '#6B7280',
};

export default function CasesList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const statusFilter = searchParams.get('status') || 'ALL';

  const load = useCallback(() => {
    setLoading(true);
    api.cases({ status: statusFilter !== 'ALL' ? statusFilter : undefined, search: search || undefined })
      .then(setCases)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (s) => {
    if (s === 'ALL') searchParams.delete('status');
    else searchParams.set('status', s);
    setSearchParams(searchParams);
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Dossiers</h1>
        <div style={styles.headerRight}>
          <div style={styles.searchBox}>
            <Search size={16} color="#888" />
            <input
              placeholder="Rechercher nom, téléphone, vol..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={styles.searchInput}
            />
          </div>
          <Link to="/cases/new" style={styles.newBtn}>
            <Plus size={16} /> Nouveau dossier
          </Link>
        </div>
      </div>

      <div style={styles.filterRow}>
        <button onClick={() => setStatus('ALL')} style={{...styles.filterBtn, ...(statusFilter === 'ALL' ? styles.filterBtnActive : {})}}>
          Tous
        </button>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setStatus(key)}
            style={{...styles.filterBtn, ...(statusFilter === key ? { ...styles.filterBtnActive, background: STATUS_COLORS[key] } : {})}}
          >
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={styles.empty}>Chargement...</p>
      ) : cases.length === 0 ? (
        <div style={styles.emptyState}>
          <Plane size={32} color="#ccc" />
          <p style={styles.empty}>Aucun dossier trouvé</p>
        </div>
      ) : (
        <div style={styles.list}>
          {cases.map((c) => (
            <Link key={c.id} to={`/cases/${c.id}`} style={styles.card}>
              <div style={styles.cardLeft}>
                <div style={styles.caseNum}>#{c.case_number}</div>
                <div>
                  <p style={styles.clientName}>{c.client_name || 'Nom non renseigné'}</p>
                  <p style={styles.cardMeta}>
                    {c.flight_number || '—'} · {c.departure_airport || '?'} → {c.arrival_airport || '?'}
                    {c.flight_date && ` · ${new Date(c.flight_date).toLocaleDateString('fr-FR')}`}
                  </p>
                </div>
              </div>
              <div style={styles.cardRight}>
                {c.total_compensation && (
                  <span style={styles.amount}>{c.total_compensation}€</span>
                )}
                {c.mandate_status === 'signed' && (
                  <span style={styles.mandateBadge} title="Mandat signé">✅</span>
                )}
                {c.mandate_status === 'pending' && (
                  <span style={styles.mandateBadge} title="Mandat en attente">⏳</span>
                )}
                <span style={{ ...styles.badge, background: `${STATUS_COLORS[c.status]}15`, color: STATUS_COLORS[c.status] }}>
                  {STATUS_LABELS[c.status] || c.status}
                </span>
                <ChevronRight size={18} color="#ccc" />
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontWeight: 800, color: '#0D1B3E', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  searchBox: {
    display: 'flex', alignItems: 'center', gap: 8, background: '#fff',
    borderRadius: 10, padding: '10px 14px', minWidth: 280,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  searchInput: { border: 'none', outline: 'none', fontSize: 14, flex: 1 },
  newBtn: {
    display: 'flex', alignItems: 'center', gap: 6, background: '#E8920A',
    color: '#fff', padding: '10px 16px', borderRadius: 10, textDecoration: 'none',
    fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
  },
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  filterBtn: {
    padding: '7px 14px', borderRadius: 100, border: '1px solid #e0e0e0',
    background: '#fff', color: '#666', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  filterBtnActive: { background: '#0D1B3E', color: '#fff', border: '1px solid transparent' },
  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: '#fff', borderRadius: 12, padding: '16px 18px',
    textDecoration: 'none', boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardLeft: { display: 'flex', alignItems: 'center', gap: 14 },
  caseNum: {
    fontSize: 13, fontWeight: 700, color: '#999', background: '#F0EDE8',
    padding: '6px 10px', borderRadius: 8, minWidth: 50, textAlign: 'center',
  },
  clientName: { fontSize: 15, fontWeight: 700, color: '#0D1B3E', margin: 0 },
  cardMeta: { fontSize: 13, color: '#888', margin: '2px 0 0' },
  cardRight: { display: 'flex', alignItems: 'center', gap: 12 },
  amount: { fontSize: 14, fontWeight: 700, color: '#0D9F57' },
  badge: { fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 100 },
  emptyState: { textAlign: 'center', padding: 60, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 },
  empty: { color: '#999', fontSize: 14, margin: 0 },
  mandateBadge: { fontSize: 16, lineHeight: 1 },
};
